"use client";

import { useId, useState } from "react";
import { trackEvent } from "@/lib/analytics";
import {
  climateFindingPath,
  climateFindingQuery,
  getClimateFinding,
  type ClimateChartId,
} from "@/lib/climate/findings";
import s from "./share-tools.module.css";

export function ShareTools({ chart, params, className = "" }: {
  chart: ClimateChartId;
  params?: Readonly<Record<string, string>>;
  className?: string;
}) {
  const id = useId();
  const [status, setStatus] = useState("");
  const [fallback, setFallback] = useState<{ label: string; value: string } | null>(null);
  const [downloading, setDownloading] = useState(false);

  function currentFinding() {
    // Read at activation time, including query changes made by another chart.
    const query = new URLSearchParams(window.location.search);
    for (const [key, value] of Object.entries(params ?? {})) query.set(key, value);
    return getClimateFinding(chart, query)!;
  }

  async function copy(embed: boolean) {
    const finding = currentFinding();
    const url = `https://peakwave.co${climateFindingPath(finding)}`;
    const value = embed
      ? `<iframe src="https://peakwave.co/climate/embed/${chart}?${climateFindingQuery(finding).replaceAll("&", "&amp;")}" title="${finding.title.replaceAll('"', "&quot;")}" width="100%" height="760" loading="lazy" referrerpolicy="no-referrer" style="border:0"></iframe>`
      : url;
    setFallback({ label: embed ? "Embed code" : "Chart link", value });
    try {
      if (!navigator.clipboard?.writeText) throw new Error("Clipboard unavailable");
      await navigator.clipboard.writeText(value);
      setStatus(embed ? "Embed code copied. Sources stay with the chart." : "Chart link copied with your selected view.");
      trackEvent("climate_chart_action", { chart, action: embed ? "copy-embed" : "copy-link" });
    } catch {
      setStatus("Copy is unavailable here. Select and copy the text below.");
    }
  }

  async function share() {
    const finding = currentFinding();
    const url = `https://peakwave.co${climateFindingPath(finding)}`;
    setFallback({ label: "Chart link", value: url });
    if (!navigator.share) {
      setStatus("Use the chart link below to share this view.");
      return;
    }
    try {
      // Called synchronously from the button activation, before any await.
      await navigator.share({ title: finding.title, text: `${finding.subtitle}. ${finding.period}. ${finding.caveat}`, url });
      setStatus("Chart shared.");
      trackEvent("climate_chart_action", { chart, action: "native-share" });
    } catch (error) {
      setStatus(error instanceof Error && error.name === "AbortError"
        ? "Sharing cancelled. Your chart link is below."
        : "Sharing is unavailable here. Copy the chart link below.");
    }
  }

  async function download(format: "png" | "svg" | "csv") {
    if (downloading) return;
    const finding = currentFinding();
    const query = new URLSearchParams(finding.parameters);
    query.set("chart", chart);
    query.set("format", format);
    const endpoint = format === "png" ? "chart-image" : "chart-export";
    setDownloading(true);
    setStatus(`Preparing ${format.toUpperCase()} download…`);
    try {
      const response = await fetch(`/api/climate/${endpoint}?${query}`, { signal: AbortSignal.timeout(20000) });
      if (!response.ok) throw new Error("Download unavailable");
      const objectUrl = URL.createObjectURL(await response.blob());
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = `peakwave-climate-${chart}.${format}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      setStatus(`${format.toUpperCase()} downloaded with sources and definitions.`);
      trackEvent("climate_chart_action", { chart, action: "download", format });
    } catch {
      setStatus("The download could not finish. Please try again; the chart data remains available on its public page.");
      setFallback({ label: "Chart link", value: `https://peakwave.co${climateFindingPath(finding)}` });
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className={`${s.tools} ${className}`}>
      <div role="group" aria-label={`Share and download ${chart.replaceAll("-", " ")} chart`}>
        <div className={s.buttons}>
          <button type="button" onClick={() => void copy(false)}>Copy chart link</button>
          <button type="button" onClick={() => void share()}>Share</button>
        </div>
        <details className={s.exports}>
          <summary>Download & embed</summary>
          <p>Take this view with you. Sources and definitions are included.</p>
          <div className={s.buttons}>
            <button type="button" onClick={() => void download("png")} disabled={downloading}>PNG image</button>
            <button type="button" onClick={() => void download("svg")} disabled={downloading}>SVG image</button>
            <button type="button" onClick={() => void download("csv")} disabled={downloading}>CSV + sources</button>
            <button type="button" onClick={() => void copy(true)}>Copy embed</button>
          </div>
        </details>
      </div>
      {status && <p className={s.status} role="status">{status}</p>}
      {fallback && <div className={s.fallback}>
        <label htmlFor={id}>{fallback.label}</label>
        <textarea id={id} readOnly value={fallback.value} rows={fallback.label === "Embed code" ? 4 : 2} onFocus={(event) => event.currentTarget.select()} />
      </div>}
    </div>
  );
}
